// Shared UI pieces for the membership tabs.
import React from "react";
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, Col, Container, FormGroup, Input, Label,
  Modal, ModalBody, ModalFooter, ModalHeader, Progress, Row, Spinner, Table,
} from "reactstrap";
import Header from "components/Headers/Header.jsx";
import "./member.css";
import { EMOJIS, currentBranch, formatDateTime, formatPhone, isOwner, memberPost, uploadImage } from "./memberApi.js";

// Page frame matching the other admin pages. ownerOnly hides content from non-MingZe users;
// those pages show every branch together, the others only the login branch.
export const PageShell = ({ title, ownerOnly, actions, children }) => {
  const branch = ownerOnly ? 'All Branches' : currentBranch();
  let body = children;
  if (ownerOnly && !isOwner()) {
    body = <Alert color="warning" className="m-4">You do not have access to this page.</Alert>;
  } else if (!sessionStorage.getItem('membertoken')) {
    body = <Alert color="warning" className="m-4">Please log out and log in again to use this page.</Alert>;
  }
  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Card className="shadow">
          <CardHeader className="border-0 d-flex align-items-center justify-content-between flex-wrap">
            <h3 className="mb-0">{title} {branch ? <Badge color="primary" className="ml-2 align-middle">{branch}</Badge> : null}</h3>
            <div>{actions}</div>
          </CardHeader>
          <CardBody className="pt-0">{body}</CardBody>
        </Card>
      </Container>
    </>
  );
};

// Reminder shown on the WhatsApp sending pages to lower the risk of the number being banned.
export const WhatsappSafetyTips = () => (
  <Alert color="warning">
    <strong>⚠️ Reminder before sending WhatsApp</strong>
    <ul className="mb-0 mt-2 pl-3">
      <li><b>Do not set the interval too short.</b> Sending too fast looks like spam and WhatsApp may ban the number. Use at least 15–30 seconds between messages.</li>
      <li><b>Save the sending WhatsApp number in your customers' phone contacts</b> (and save your customers in the sending phone's contacts). Messages between saved contacts are much less likely to be reported or blocked.</li>
    </ul>
  </Alert>
);

export const StatusBadge = ({ status }) => {
  const colors = {
    Active: 'success', Inactive: 'secondary', Sending: 'info', Completed: 'success', Cancelled: 'secondary',
    Draft: 'secondary', Sent: 'success', Failed: 'danger', Pending: 'warning', open: 'success', connecting: 'warning', close: 'danger',
  };
  const labels = { open: 'Connected', connecting: 'Waiting for scan', close: 'Disconnected', not_found: 'Missing', unknown: 'Unknown' };
  return <Badge color={colors[status] || 'secondary'} pill>{labels[status] || status}</Badge>;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Tickable member table with search, birthday month filter and "select all". `canSelect(member)`
// greys out members who cannot receive the message (no consent / no email).
export class MemberPicker extends React.Component {
  state = { search: '', month: '' };

  filtered() {
    const term = this.state.search.trim().toLowerCase();
    const month = this.state.month;
    return this.props.members.filter(m =>
      (!term || (m.name + ' ' + m.memberid + ' ' + m.phone + ' ' + m.email).toLowerCase().indexOf(term) !== -1) &&
      (!month || (m.birthday && m.birthday.slice(5, 7) === month)));
  }

  toggle(memberid) {
    const selected = Object.assign({}, this.props.selected);
    if (selected[memberid]) delete selected[memberid]; else selected[memberid] = true;
    this.props.onChange(selected);
  }

  selectAll(on) {
    const selected = Object.assign({}, this.props.selected);
    this.filtered().forEach(m => {
      if (!on) delete selected[m.memberid];
      else if (this.props.canSelect(m)) selected[m.memberid] = true;
    });
    this.props.onChange(selected);
  }

  render() {
    const list = this.filtered();
    const selectable = list.filter(this.props.canSelect);
    const allOn = selectable.length > 0 && selectable.every(m => this.props.selected[m.memberid]);
    const anyOn = list.some(m => this.props.selected[m.memberid]);
    const count = Object.keys(this.props.selected).length;
    return (
      <div>
        <Row className="align-items-center mb-2">
          <Col md="5"><Input placeholder="Search name, member ID, phone, email" value={this.state.search} onChange={e => this.setState({ search: e.target.value })} /></Col>
          <Col md="3" className="mt-2 mt-md-0">
            <Input type="select" value={this.state.month} onChange={e => this.setState({ month: e.target.value })}>
              <option value="">All birthday months</option>
              {MONTHS.map((name, i) => <option key={name} value={String(i + 1).padStart(2, '0')}>Birthday in {name}</option>)}
            </Input>
          </Col>
          <Col md="4" className="text-md-right mt-2 mt-md-0">
            <strong>{count}</strong> selected &nbsp;
            <Button size="sm" color="secondary" onClick={() => this.selectAll(true)} disabled={allOn}>Tick all shown</Button>
            <Button size="sm" color="secondary" onClick={() => this.selectAll(false)} disabled={!anyOn}>Untick all shown</Button>
          </Col>
        </Row>
        <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: 6 }}>
          <Table size="sm" className="align-items-center mb-0" hover>
            <thead className="thead-light" style={{ position: 'sticky', top: 0 }}>
              <tr><th style={{ width: 40 }}></th><th>Member ID</th><th>Branch</th><th>Name</th><th>{this.props.contactLabel}</th><th>Birthday</th><th></th></tr>
            </thead>
            <tbody>
              {list.map(m => {
                const ok = this.props.canSelect(m);
                return (
                  <tr key={m.memberid} onClick={() => ok && this.toggle(m.memberid)} style={{ cursor: ok ? 'pointer' : 'default', opacity: ok ? 1 : 0.45 }}>
                    <td><input type="checkbox" disabled={!ok} checked={!!this.props.selected[m.memberid]} readOnly /></td>
                    <td>{m.memberid}</td>
                    <td>{m.server || '-'}</td>
                    <td>{m.name}</td>
                    <td>{this.props.contactOf(m)}</td>
                    <td>{m.birthday ? m.birthday.slice(5).split('-').reverse().join('/') : ''}</td>
                    <td>{ok ? '' : <small className="text-muted">{this.props.reasonOf(m)}</small>}</td>
                  </tr>
                );
              })}
              {list.length === 0 ? <tr><td colSpan="7" className="text-center text-muted py-4">No members</td></tr> : null}
            </tbody>
          </Table>
        </div>
      </div>
    );
  }
}

// Textarea with emoji bar, placeholder buttons and optional image upload.
export class MessageComposer extends React.Component {
  state = { uploading: false, uploaderror: '' };
  textarea = React.createRef();

  insert(text) {
    const el = this.textarea.current;
    const value = this.props.value || '';
    if (!el) { this.props.onChange(value + text); return; }
    const start = el.selectionStart, end = el.selectionEnd;
    this.props.onChange(value.slice(0, start) + text + value.slice(end));
    setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = start + text.length; }, 0);
  }

  onFile = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { this.setState({ uploaderror: 'Image must be smaller than 5MB' }); return; }
    this.setState({ uploading: true, uploaderror: '' });
    uploadImage(file).then(json => {
      if (json.success == 1) { this.setState({ uploading: false }); this.props.onImageChange(json.url); }
      else this.setState({ uploading: false, uploaderror: json.message || 'Upload failed' });
    });
  };

  render() {
    return (
      <div>
        <div className="mb-2 d-flex flex-wrap align-items-center" style={{ gap: 4 }}>
          {EMOJIS.map(e => (
            <button key={e} type="button" onClick={() => this.insert(e)} style={{ border: 'none', background: '#f6f9fc', borderRadius: 4, fontSize: 18, padding: '2px 6px', cursor: 'pointer' }}>{e}</button>
          ))}
          <span className="mx-2 text-muted">|</span>
          <Button size="sm" outline color="primary" onClick={() => this.insert('{name}')}>+ Name</Button>
          <Button size="sm" outline color="primary" onClick={() => this.insert('{memberid}')}>+ Member ID</Button>
          {this.props.whatsapp ? (
            <>
              <Button size="sm" outline color="secondary" onClick={() => this.insert('*bold*')}><b>B</b></Button>
              <Button size="sm" outline color="secondary" onClick={() => this.insert('_italic_')}><i>I</i></Button>
            </>
          ) : null}
        </div>
        <Input innerRef={this.textarea} type="textarea" rows={this.props.rows || 8} value={this.props.value} onChange={e => this.props.onChange(e.target.value)}
          placeholder={this.props.placeholder} style={{ fontSize: 15 }} />
        <small className="text-muted">{'{name}'} and {'{memberid}'} are replaced with each member's details. Windows: press Win + . for more emoji.</small>
        <div className="mt-3">
          <Label className="form-control-label d-block">Image (optional)</Label>
          {this.props.imageurl ? (
            <div className="d-flex align-items-start" style={{ gap: 12 }}>
              <img src={this.props.imageurl} alt="" style={{ maxWidth: 200, maxHeight: 200, borderRadius: 6, border: '1px solid #e9ecef' }} />
              <Button size="sm" color="danger" outline onClick={() => this.props.onImageChange('')}>Remove image</Button>
            </div>
          ) : (
            <div>
              <input type="file" accept="image/jpeg,image/png,image/gif" onChange={this.onFile} disabled={this.state.uploading} />
              {this.state.uploading ? <Spinner size="sm" className="ml-2" /> : null}
            </div>
          )}
          {this.state.uploaderror ? <small className="text-danger d-block">{this.state.uploaderror}</small> : null}
          {this.props.whatsapp && this.props.imageurl ? <small className="text-muted d-block">With an image, the message is sent as the image caption.</small> : null}
        </div>
      </div>
    );
  }
}

// Lists WhatsApp instances created from this system; add, scan QR, delete.
export class WhatsappInstances extends React.Component {
  state = { instances: [], loading: true, newname: '', creating: false, error: '', qr: null };

  componentDidMount() { this.load(); }
  componentWillUnmount() { clearTimeout(this.pollTimer); this.unmounted = true; }

  load = () => {
    this.setState({ loading: true });
    memberPost('wa-instance-list').then(json => {
      if (this.unmounted) return;
      const instances = json.success == 1 ? json.instances : [];
      this.setState({ instances, loading: false, error: json.success == 1 ? '' : json.message });
      if (this.props.onLoad) this.props.onLoad(instances);
    });
  };

  create = () => {
    if (!this.state.newname.trim()) { this.setState({ error: 'Please key in a name, e.g. Shop Phone' }); return; }
    this.setState({ creating: true, error: '' });
    memberPost('wa-instance-create', { displayname: this.state.newname.trim() }).then(json => {
      this.setState({ creating: false });
      if (json.success != 1) { this.setState({ error: json.message }); return; }
      this.setState({ newname: '' });
      this.load();
      this.openQr(json.instancename, json.qrcode);
    });
  };

  openQr = (instancename, qrcode) => {
    this.setState({ qr: { instancename, qrcode: qrcode || '', state: 'connecting' } });
    if (!qrcode) this.refreshQr(instancename);
    this.pollState();
  };

  refreshQr = (instancename) => {
    memberPost('wa-instance-connect', { instancename }).then(json => {
      if (!this.state.qr) return;
      if (json.state === 'open') { this.connected(); return; }
      this.setState({ qr: Object.assign({}, this.state.qr, { qrcode: json.qrcode || this.state.qr.qrcode }) });
    });
  };

  pollState = () => {
    clearTimeout(this.pollTimer);
    this.pollTimer = setTimeout(() => {
      if (!this.state.qr || this.unmounted) return;
      const name = this.state.qr.instancename;
      memberPost('wa-instance-state', { instancename: name }).then(json => {
        if (!this.state.qr) return;
        if (json.state === 'open') this.connected();
        else {
          // QR codes expire after ~40s; fetch a fresh one every few polls.
          this.qrPolls = (this.qrPolls || 0) + 1;
          if (this.qrPolls % 8 === 0) this.refreshQr(name);
          this.pollState();
        }
      });
    }, 3000);
  };

  connected = () => {
    clearTimeout(this.pollTimer);
    this.setState({ qr: Object.assign({}, this.state.qr, { state: 'open' }) });
    this.load();
  };

  closeQr = () => { clearTimeout(this.pollTimer); this.setState({ qr: null }); this.load(); };

  remove = (inst) => {
    if (!window.confirm('Delete WhatsApp instance "' + inst.displayname + '"? It will be logged out from WhatsApp.')) return;
    memberPost('wa-instance-delete', { instancename: inst.instancename }).then(json => {
      if (json.success != 1) window.alert(json.message);
      this.load();
    });
  };

  render() {
    const s = this.state;
    return (
      <div>
        <Row className="align-items-end">
          <Col md="5">
            <FormGroup className="mb-2">
              <Label className="form-control-label">Register new WhatsApp instance</Label>
              <Input placeholder="Name, e.g. Shop Phone 1" value={s.newname} onChange={e => this.setState({ newname: e.target.value })} maxLength={50} />
            </FormGroup>
          </Col>
          <Col md="3" className="mb-2">
            <Button color="success" onClick={this.create} disabled={s.creating}>{s.creating ? <Spinner size="sm" /> : 'Register & Scan QR'}</Button>
          </Col>
          <Col md="4" className="mb-2 text-md-right">
            <Button size="sm" color="secondary" onClick={this.load}>Refresh status</Button>
          </Col>
        </Row>
        {s.error ? <Alert color="danger" className="py-2">{s.error}</Alert> : null}
        <Table size="sm" className="mt-2" responsive>
          <thead className="thead-light"><tr><th>Name</th><th>WhatsApp Number</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {s.loading ? <tr><td colSpan="4" className="text-center py-3"><Spinner size="sm" /></td></tr> :
              s.instances.length === 0 ? <tr><td colSpan="4" className="text-center text-muted py-3">No WhatsApp instance registered yet</td></tr> :
                s.instances.map(inst => (
                  <tr key={inst.instancename}>
                    <td>{inst.displayname}</td>
                    <td>{inst.number ? formatPhone(inst.number) : '-'}</td>
                    <td><StatusBadge status={inst.state} /></td>
                    <td className="text-right">
                      {inst.state !== 'open' ? <Button size="sm" color="primary" onClick={() => this.openQr(inst.instancename, '')}>Scan QR</Button> : null}
                      <Button size="sm" color="danger" outline onClick={() => this.remove(inst)}>Delete</Button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </Table>

        <Modal isOpen={!!s.qr} toggle={this.closeQr} centered>
          <ModalHeader toggle={this.closeQr}>Connect WhatsApp</ModalHeader>
          <ModalBody className="text-center">
            {s.qr && s.qr.state === 'open' ? (
              <div className="py-4"><div style={{ fontSize: 48 }}>✅</div><h3>WhatsApp connected</h3></div>
            ) : (
              <>
                <p>On the phone: WhatsApp → <b>Linked devices</b> → <b>Link a device</b>, then scan this QR code.</p>
                {s.qr && s.qr.qrcode ? <img src={s.qr.qrcode} alt="WhatsApp QR" style={{ width: 280, height: 280 }} /> : <div className="py-5"><Spinner /></div>}
                <p className="text-muted mt-2 mb-0"><small>Waiting for scan… this window updates automatically.</small></p>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {s.qr && s.qr.state !== 'open' ? <Button color="secondary" onClick={() => this.refreshQr(s.qr.instancename)}>New QR code</Button> : null}
            <Button color="primary" onClick={this.closeQr}>Close</Button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }
}

// Full-screen progress while a campaign sends. Polls campaign-progress (which also drives the queue).
export class CampaignProgress extends React.Component {
  state = { campaign: null, error: '' };

  componentDidMount() { this.poll(); }
  componentWillUnmount() { this.unmounted = true; clearTimeout(this.timer); }

  poll = () => {
    memberPost('campaign-progress', { campaignid: this.props.campaignid }).then(json => {
      if (this.unmounted) return;
      if (json.success == 1) {
        this.setState({ campaign: json.campaign, error: '' });
        if (json.campaign.status === 'Sending') this.timer = setTimeout(this.poll, 1500);
      } else {
        this.setState({ error: json.message || 'Lost connection, retrying…' });
        this.timer = setTimeout(this.poll, 5000);
      }
    });
  };

  cancel = () => {
    if (!window.confirm('Stop sending? Messages not yet sent will be cancelled.')) return;
    memberPost('campaign-cancel', { campaignid: this.props.campaignid }).then(() => this.poll());
  };

  render() {
    const c = this.state.campaign;
    const total = c ? parseInt(c.totalcount, 10) : 0;
    const done = c ? parseInt(c.sentcount, 10) + parseInt(c.failedcount, 10) : 0;
    const pct = total ? Math.round(done * 100 / total) : 0;
    const finished = c && c.status !== 'Sending';
    return (
      <Modal isOpen centered size="lg" backdrop="static" keyboard={false}>
        <ModalHeader>{c ? c.name : 'Starting…'}</ModalHeader>
        <ModalBody>
          {!c ? <div className="text-center py-4"><Spinner /></div> : (
            <>
              <div className="d-flex justify-content-between mb-1">
                <span><StatusBadge status={c.status} /> {finished ? '' : 'Sending… please keep this page open for the fastest delivery.'}</span>
                <strong>{done} / {total}</strong>
              </div>
              <Progress multi style={{ height: 22 }}>
                <Progress bar color="success" value={total ? c.sentcount * 100 / total : 0} animated={!finished}>{pct}%</Progress>
                <Progress bar color="danger" value={total ? c.failedcount * 100 / total : 0} />
              </Progress>
              <Row className="text-center mt-3">
                <Col><h2 className="text-success mb-0">{c.sentcount}</h2><small>Sent</small></Col>
                <Col><h2 className="text-danger mb-0">{c.failedcount}</h2><small>Failed</small></Col>
                <Col><h2 className="mb-0">{Math.max(0, total - done)}</h2><small>Remaining</small></Col>
              </Row>
              {!finished && c.nextsendat ? <p className="text-muted text-center mt-2 mb-0"><small>Messages are spaced out by the interval you set. If you close this page, sending continues in the background.</small></p> : null}
              {c.failedlist && c.failedlist.length ? (
                <div className="mt-3" style={{ maxHeight: 160, overflowY: 'auto' }}>
                  <Table size="sm"><thead><tr><th>Failed</th><th>To</th><th>Reason</th></tr></thead>
                    <tbody>{c.failedlist.map((f, i) => <tr key={i}><td>{f.recipientname}</td><td>{f.recipient}</td><td><small>{f.error}</small></td></tr>)}</tbody>
                  </Table>
                </div>
              ) : null}
            </>
          )}
          {this.state.error ? <Alert color="warning" className="mt-2 py-2">{this.state.error}</Alert> : null}
        </ModalBody>
        <ModalFooter>
          {finished ? <Button color="primary" onClick={this.props.onClose}>Done</Button> :
            <Button color="danger" outline onClick={this.cancel} disabled={!c}>Stop sending</Button>}
        </ModalFooter>
      </Modal>
    );
  }
}

// Past campaigns with a per-recipient detail view.
export class CampaignHistory extends React.Component {
  state = { campaigns: [], loading: true, detail: null };

  componentDidMount() { this.load(); }

  load = () => {
    this.setState({ loading: true });
    memberPost('campaign-list', { channel: this.props.channel, campaigntype: this.props.campaigntype || 'blast' })
      .then(json => this.setState({ campaigns: json.success == 1 ? json.campaigns : [], loading: false }));
  };

  openDetail = (c) => {
    this.setState({ detail: { campaign: c, messages: null } });
    memberPost('campaign-detail', { campaignid: c.Ref })
      .then(json => this.setState({ detail: { campaign: c, messages: json.success == 1 ? json.messages : [] } }));
  };

  render() {
    const s = this.state;
    return (
      <div>
        <div className="text-right mb-2"><Button size="sm" color="secondary" onClick={this.load}>Refresh</Button></div>
        <Table size="sm" responsive hover>
          <thead className="thead-light"><tr><th>Date</th><th>Campaign</th><th>Status</th><th>Sent</th><th>Failed</th><th>Total</th><th></th></tr></thead>
          <tbody>
            {s.loading ? <tr><td colSpan="7" className="text-center py-3"><Spinner size="sm" /></td></tr> :
              s.campaigns.length === 0 ? <tr><td colSpan="7" className="text-center text-muted py-3">No campaigns yet</td></tr> :
                s.campaigns.map(c => (
                  <tr key={c.Ref}>
                    <td>{formatDateTime(c.startedat || c.dateCreate)}</td>
                    <td>{c.name}</td>
                    <td><StatusBadge status={c.status} /></td>
                    <td>{c.sentcount}</td><td>{c.failedcount}</td><td>{c.totalcount}</td>
                    <td className="text-right">
                      {c.status === 'Sending' && this.props.onResume ? <Button size="sm" color="info" onClick={() => this.props.onResume(c.Ref)}>Progress</Button> : null}
                      <Button size="sm" color="secondary" onClick={() => this.openDetail(c)}>Details</Button>
                      {this.props.onReuse ? <Button size="sm" color="primary" outline onClick={() => this.props.onReuse(c)}>Reuse</Button> : null}
                    </td>
                  </tr>
                ))}
          </tbody>
        </Table>

        <Modal isOpen={!!s.detail} toggle={() => this.setState({ detail: null })} size="lg">
          <ModalHeader toggle={() => this.setState({ detail: null })}>{s.detail ? s.detail.campaign.name : ''}</ModalHeader>
          <ModalBody>
            {s.detail ? (
              <>
                {s.detail.campaign.subject ? <p><b>Subject:</b> {s.detail.campaign.subject}</p> : null}
                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', background: '#f6f9fc', padding: 12, borderRadius: 6 }}>{s.detail.campaign.content}</pre>
                {s.detail.campaign.imageurl ? <img src={s.detail.campaign.imageurl} alt="" style={{ maxWidth: 200, borderRadius: 6 }} /> : null}
                {s.detail.messages === null ? <div className="text-center py-3"><Spinner size="sm" /></div> : (
                  <div style={{ maxHeight: 360, overflowY: 'auto' }} className="mt-3">
                    <Table size="sm">
                      <thead className="thead-light"><tr><th>Member</th><th>To</th><th>Status</th><th>Time</th><th>Error</th></tr></thead>
                      <tbody>{s.detail.messages.map((m, i) => (
                        <tr key={i}><td>{m.recipientname} <small className="text-muted">{m.memberid}</small></td><td>{m.recipient}</td>
                          <td><StatusBadge status={m.status} /></td><td>{formatDateTime(m.sentat)}</td><td><small>{m.error}</small></td></tr>
                      ))}</tbody>
                    </Table>
                  </div>
                )}
              </>
            ) : null}
          </ModalBody>
        </Modal>
      </div>
    );
  }
}
